import * as React from 'react';
import { useRef, useState } from 'react';
import { addVoucherOrPromotionToTransaction } from 'Services/Api/_HumanWritten/GiftCertificateService';
import { fetchGiftCertificateUsagesByTransactionId } from 'Util/_HumanWritten/FetchGiftCertificates';
import { GiftCertificateUsageEntity } from 'Models/Entities';
import { observer } from 'mobx-react';
import PromoGiftCardInput, {
	GiftCertificateProps,
	PromoGiftCardMessage,
} from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Cart/PromoGiftCardInput';
import {
	PROMOTION_NOT_ELIGIBLE_CUSTOMER_MESSAGE,
	PROMOTION_PARTIALLY_APPLIED_USAGE_LIMIT_MESSAGE,
	PROMOTION_USAGE_LIMIT_REACHED_MESSAGE,
} from 'Util/_HumanWritten/PromotionErrorMessages';

export interface FerryPromoGiftCardProps {
	transactionId: string;
	/**
	 * Refreshes whatever is showing the money — the cart's totals, or the check-in review's.
	 *
	 * <p>Awaited, so a handler that returns a promise holds the "Applying voucher" state until the
	 * new totals are actually on screen. The voucher is not applied, as far as the person looking at
	 * it is concerned, until the number they are reading has moved: the POST returning only means the
	 * server has taken it. A handler that returns nothing behaves as it always did.</p>
	 */
	onAddGiftCertificate?: () => void | Promise<unknown>;
	/**
	 * If defined, list of GiftCertificateUsageEntity related to transactionId is fetched and passed into this
	 * callback function.
	 */
	updateGiftCertUsages?: (newEntities: GiftCertificateUsageEntity[]) => Promise<void>;
	forCheckIn: boolean;
}

function FerryPromoGiftCard({
	transactionId,
	onAddGiftCertificate,
	updateGiftCertUsages,
	forCheckIn,
}: FerryPromoGiftCardProps) {
	const [certificateProps, setCertificateProps] = useState<GiftCertificateProps>({
		code: '',
		loading: false,
	});
	const [message, setMessage] = useState<PromoGiftCardMessage | null>(null);
	const applyingRef = useRef(false);

	const fetchUsages = async () => {
		try {
			const { data } = await fetchGiftCertificateUsagesByTransactionId(transactionId);
			return data ?? [];
		} catch (e) {
			console.error(e);
			return [];
		}
	};

	const onApply = async () => {
		if (applyingRef.current) {
			return;
		}
		applyingRef.current = true;

		// Start loading
		setCertificateProps(prevState => ({
			...prevState,
			loading: true,
		}));

		try {
			await addVoucherOrPromotionToTransaction(transactionId, certificateProps.code, forCheckIn, false);

			// Awaited before the spinner comes down — see onAddGiftCertificate. Failing to refresh is
			// not failing to apply: the voucher is on the transaction either way, so a refresh that
			// throws still reports success rather than telling the customer to try again and apply it
			// twice.
			try {
				await onAddGiftCertificate?.();
			} catch (refreshError) {
				console.error(refreshError);
			}

			if (updateGiftCertUsages) {
				const usages = await fetchUsages();
				await updateGiftCertUsages(usages);
			}

			// Reset state
			applyingRef.current = false;
			setCertificateProps({ code: '', loading: false });
			setMessage({ text: 'Voucher applied', type: 'success' });
		} catch (e: any) {
			const errorCode: string = e.response?.data ?? '';
			let messageText: string = 'Invalid voucher';
			let messageType: PromoGiftCardMessage['type'] = 'error';
			let halfApplied: boolean = false;

			// Reset state
			applyingRef.current = false;
			setCertificateProps({ code: '', loading: false });

			if (errorCode.includes('trip departure window')) {
				messageText = errorCode;
				if (!errorCode.includes('No bookings on the transaction')) {
					messageType = 'warning';
					halfApplied = true;
				}
			} else {
				switch (errorCode) {
					// Voucher specific error messages
					case 'BAD_CODE':
						messageText = 'Gift certificate does not exist';
						break;
					case 'EMPTY':
						messageText = 'Gift certificate has no remaining value';
						break;
					case 'EXPIRED':
						messageText = 'Gift certificate is expired';
						break;
					// Shared: the server answers ALREADY_APPLIED for a gift certificate as well as for a
					// promotion, and the code alone does not say which, so the wording covers both.
					case 'ALREADY_APPLIED':
						messageText = 'This code is already applied to this transaction';
						break;
					case 'CONFLICT_OTHER_DISCOUNT':
						messageText =
							'There is a promotion already applied to this transaction that does not allow other promotions. Please remove the other promotion first.';
						break;
					case 'CONFLICT_THIS_DISCOUNT':
						messageText =
							'This promotion can not be applied with other promotions. Please remove the other promotion/s first.';
						break;
					case 'USAGE_LIMIT_REACHED':
						messageText = PROMOTION_USAGE_LIMIT_REACHED_MESSAGE;
						break;
					case 'NOT_ELIGIBLE_CUSTOMER':
						messageText = PROMOTION_NOT_ELIGIBLE_CUSTOMER_MESSAGE;
						break;
					case 'INACTIVE':
						messageText = 'Promotion is no longer active';
						break;
					case 'NOT_FERRY_BOOKING':
						messageText = 'This promotion can not be applied to a ferry booking.';
						break;
					case 'NOT_EVENT_BOOKING':
						messageText = 'This promotion can not be applied to an event booking.';
						break;
					case 'NO_VALID_BOOKINGS':
						messageText = 'This promotion can not be applied to the selected bookings.';
						break;
					case 'BOOKING_CREATION_ONLY':
						messageText = 'This promotion can only be applied to new bookings.';
						break;
					case 'BOOKING_ALTER_ONLY':
						messageText = 'This promotion can only be applied when altering an existing booking.';
						break;
					case 'RETURN_BOOKING_DISCOUNT':
						messageText = 'This promotion can only be applied to return bookings.';
						break;
					case 'ONE_WAY_BOOKING_DISCOUNT':
						messageText = 'This promotion can only be applied to one-way bookings.';
						break;
					case 'NOT_SAME_DAY_RETURN':
						messageText =
							'This promotion only applies when both legs of a return booking depart on the same day.';
						break;
					case 'INVALID_BOOKING_TYPE':
						messageText = 'This promotion can only be applied to certain booking types.';
						break;
					case 'PARTIALLY_APPLIED':
						messageText = 'This promotion was only applied to the bookings that it applies to.';
						messageType = 'warning';
						halfApplied = true;
						break;
					case 'PARTIALLY_APPLIED_USAGE_LIMIT':
						messageText = PROMOTION_PARTIALLY_APPLIED_USAGE_LIMIT_MESSAGE;
						messageType = 'warning';
						halfApplied = true;
						break;
					case 'SAVE_FAILED':
						messageText = "Couldn't save the promotion right now. Please try again.";
						break;
					// Shared error messages
					case 'NOT_REQUIRED':
						messageText = 'Transaction does not require payment';
						break;
					case 'NOT_APPLICABLE':
						messageText = 'This promotion does not reduce the price of this booking.';
						messageType = 'warning';
						break;
					// Final fallback error (if no matching voucher or promotion is found)
					case 'INVALID_CODE':
						messageText = 'Invalid voucher or promotion code';
						break;
				}
			}

			setMessage({ text: messageText, type: messageType });

			if (halfApplied) {
				try {
					await onAddGiftCertificate?.();
				} catch (refreshError) {
					console.error(refreshError);
				}
				if (updateGiftCertUsages) {
					const usages = await fetchUsages();
					await updateGiftCertUsages(usages);
				}
			}
		}
	};

	return (
		<PromoGiftCardInput
			certificateProps={certificateProps}
			setCertificateProps={setCertificateProps}
			onApply={onApply}
			message={message}
			onCodeChange={() => setMessage(null)}
		/>
	);
}

export default observer(FerryPromoGiftCard);
